/**
 * viewportScreenshot.js — Enterprise Screenshot & Export Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE SCREENSHOT SUITE]:
 *  - readPixels: PACK_ALIGNMENT = 1 (защита от некратных ширин)
 *  - batchExportSlices: setSliceIndex(0) вместо цикла scroll(-1)
 *  - ZIP экспорт батча (через динамический импорт JSZip)
 *  - Animated GIF из CINE (через gif.js)
 *  - WebP поддержка (формат 'webp' в screenshotViewport)
 *  - Overlay customisation (показывать/скрывать orientation, patient, timestamp)
 *  - Аннотации на скриншоте (наложение текущих измерений)
 *  - Скриншот в буфер с рамкой (RadiAnt-style)
 *  - Сравнение до/после (два скриншота рядом с разметкой)
 *  - Screen recording (MediaRecorder из canvas)
 *  - Bulk export metadata (CSV с координатами и HU)
 *
 * @module viewportScreenshot
 */

import state, { getRenderingEngine, getViewportIds, OrientationAxis, cornerstone, cornerstoneTools } from './viewportState.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { КОРОТКО, СТРОКА } from './disclaimer.js';   // [10.19.9] оговорка уезжает вместе с числами
import { АДРЕС_РАБОЧЕГО_ФАЙЛА } from './gifWorkerUrl.js';   // [10.19.10] адрес gif.worker от сборщика

// ══════════════════════════════════════════════════════════════
//  CORE SCREENSHOT
// ══════════════════════════════════════════════════════════════

export function screenshotViewport(viewportId, opts = {}) {
  const format = opts.format || 'png';
  const quality = opts.quality || 0.95;
  const drawOverlay = opts.overlay !== false;
  const overlayOpts = opts.overlayOptions || {};

  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return null;

  const offscreen = document.createElement('canvas');
  offscreen.width = canvas.width;
  offscreen.height = canvas.height;
  const ctx = offscreen.getContext('2d');

  try {
    ctx.drawImage(canvas, 0, 0);
  } catch {
    // WebGL readPixels fallback
    try {
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      if (!gl) return null;

      gl.pixelStorei(gl.PACK_ALIGNMENT, 1); // фикс для некратных ширин

      const w = canvas.width, h = canvas.height;
      const pixels = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

      const imgData = ctx.createImageData(w, h);
      for (let y = 0; y < h; y++) {
        const src = (h - y - 1) * w * 4;
        imgData.data.set(pixels.subarray(src, src + w * 4), y * w * 4);
      }
      ctx.putImageData(imgData, 0, 0);
    } catch (e2) {
      log.error('[Screenshot] readPixels failed:', e2.message);
      return null;
    }
  }

  if (drawOverlay) _drawOverlay(ctx, offscreen.width, offscreen.height, viewportId, opts.patientInfo, overlayOpts);
  if (opts.watermark) _drawWatermark(ctx, offscreen.width, offscreen.height, opts.watermark);

  // Поддержка WebP
  const mimeType = format === 'webp' ? 'image/webp' : `image/${format}`;
  return offscreen.toDataURL(mimeType, quality);
}

export function downloadScreenshot(viewportId, filename, opts = {}) {
  const dataUrl = screenshotViewport(viewportId, opts);
  if (!dataUrl) { showToast('❌ Не удалось сделать скриншот', 'error', 3000); return; }
  _downloadDataUrl(dataUrl, filename || `viewport-${viewportId}-${Date.now()}.png`);
  showToast('📸 Скриншот сохранён', 'success', 2000);
}

export async function copyScreenshotToClipboard(viewportId, opts = {}) {
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId || getViewportIds()[0]);
    if (!vp) return false;
    const canvas = vp.canvas || vp.element?.querySelector('canvas');
    if (!canvas) return false;

    const blob = await new Promise((resolve, reject) => {
      if (canvas.toBlob) {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
      } else {
        // Fallback для Safari
        const dataUrl = canvas.toDataURL('image/png');
        fetch(dataUrl).then(r => r.blob()).then(resolve).catch(reject);
      }
    });
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    showToast('📋 Скопировано в буфер', 'success', 1500);
    return true;
  } catch (e) {
    log.warn('[Screenshot] Clipboard:', e.message);
    showToast('❌ Не удалось скопировать', 'error', 2000);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
//  SCREENSHOT С РАМКОЙ (RadiAnt-style)
// ══════════════════════════════════════════════════════════════

/**
 * Скриншот с белой рамкой и подписью как в RadiAnt.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {string} [opts.title] — заголовок под скриншотом
 * @param {Object} [opts.patientInfo] — информация о пациенте
 * @param {number} [opts.borderPx=20] — ширина рамки
 */
export function screenshotWithFrame(viewportId, opts = {}) {
  const dataUrl = screenshotViewport(viewportId, { ...opts, overlay: false });
  if (!dataUrl) return null;

  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      const border = opts.borderPx || 20;
      const titleHeight = opts.title ? 40 : 0;
      // [10.19.9] Оговорка печатается на самом снимке. Футер программы в файл не
      // уезжает, а числа на снимке — уезжают: через месяц такой PNG выглядит как
      // документ. Отказаться от неё можно только явно (opts.disclaimer === false),
      // чтобы это было видно в коде вызывающего, а не получалось само собой.
      const оговорка = opts.disclaimer === false ? '' : (opts.disclaimerText || СТРОКА);
      const шрифтОговорки = 11;
      const оговоркаH = оговорка ? шрифтОговорки + 10 : 0;
      const canvas = document.createElement('canvas');
      canvas.width = img.width + border * 2;
      canvas.height = img.height + border * 2 + titleHeight + оговоркаH;
      const ctx = canvas.getContext('2d');

      // Белая рамка
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Изображение
      ctx.drawImage(img, border, border);

      // Заголовок
      if (opts.title) {
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(opts.title, canvas.width / 2, img.height + border + 28);
      }

      // Оговорка — мелким, но читаемым, и с переносом, если не влезает.
      if (оговорка) {
        ctx.fillStyle = '#8a6d00';
        ctx.font = `${шрифтОговорки}px sans-serif`;
        ctx.textAlign = 'center';
        const y = canvas.height - 8;
        const макс = canvas.width - 12;
        if (ctx.measureText(оговорка).width <= макс) {
          ctx.fillText(оговорка, canvas.width / 2, y);
        } else {
          // Не влезло — оставляем короткую форму, обрезать смысл нельзя.
          ctx.font = `bold ${шрифтОговорки}px sans-serif`;
          ctx.fillText(КОРОТКО, canvas.width / 2, y);
        }
      }

      resolve(canvas.toDataURL('image/png', 0.95));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ══════════════════════════════════════════════════════════════
//  COLLAGE
// ══════════════════════════════════════════════════════════════

export async function screenshotAll(opts = {}) {
  try {
    const ids = getViewportIds();
    if (!ids.length) return null;
    const shots = ids
      .map(id => ({ id, dataUrl: screenshotViewport(id, { ...opts, overlay: true }) }))
      .filter(s => s.dataUrl);
    if (!shots.length) return null;

    const cols = shots.length <= 2 ? shots.length : Math.ceil(Math.sqrt(shots.length));
    const images = await Promise.all(shots.map(s =>
      new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = s.dataUrl; })
    ));
    const valid = images.filter(Boolean);
    if (!valid.length) return null;

    const cellW = valid[0].width, cellH = valid[0].height;
    const rows = Math.ceil(valid.length / cols);
    const collage = document.createElement('canvas');
    collage.width = cellW * cols;
    collage.height = cellH * rows;
    const ctx = collage.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, collage.width, collage.height);
    valid.forEach((im, i) => ctx.drawImage(im, (i % cols) * cellW, Math.floor(i / cols) * cellH));

    const format = opts.format === 'webp' ? 'image/webp' : `image/${opts.format || 'png'}`;
    return collage.toDataURL(format, opts.quality || 0.95);
  } catch (e) { log.warn(`[viewportScreenshot] screenshotAll failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  COMPARISON STRIP
// ══════════════════════════════════════════════════════════════

export async function createComparisonStrip(viewportId, numFrames = 6, opts = {}) {
  try { (await import('../ui/exportModule.js')).отметитьЭкспорт('лента'); } catch (e) { /* история — не повод */ }
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId);
    if (!vp) return null;

    const isStack = typeof vp.setImageIdIndex === 'function';
    let total = isStack ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 100);
    if (!total) return null;

    const originalIdx = isStack ? (vp.getCurrentImageIdIndex?.() || 0) : (vp.getSliceIndex?.() || 0);
    const step = Math.max(1, Math.floor(total / numFrames));
    const frames = [];

    if (isStack) {
      for (let i = 0; i < numFrames && i * step < total; i++) {
        const idx = i * step;
        vp.setImageIdIndex(idx);
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false });
        if (shot) frames.push({ dataUrl: shot, index: idx });
      }
      vp.setImageIdIndex(originalIdx);
    } else {
      // Быстрый переход к началу
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(0); } catch {}
      vp.render();
      await new Promise(r => setTimeout(r, 80));

      for (let i = 0; i < numFrames && i * step < total; i++) {
        const targetSlice = i * step;
        try {
          if (typeof vp.setSliceIndex === 'function') {
            vp.setSliceIndex(targetSlice);
          } else {
            vp.scroll(targetSlice - (vp.getSliceIndex?.() || 0));
          }
        } catch {}
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false });
        if (shot) frames.push({ dataUrl: shot, index: targetSlice });
      }
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(originalIdx); } catch {}
    }
    vp.render();

    if (!frames.length) return null;

    const images = await Promise.all(frames.map(f =>
      new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = f.dataUrl; })
    ));
    const valid = images.filter(Boolean);
    if (!valid.length) return null;

    const frameW = opts.width ? Math.round(opts.width / valid.length) : valid[0].width;
    const frameH = Math.round(frameW * valid[0].height / valid[0].width);
    const strip = document.createElement('canvas');
    strip.width = frameW * valid.length;
    strip.height = frameH + 24;
    const ctx = strip.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, strip.width, strip.height);

    valid.forEach((img, i) => {
      ctx.drawImage(img, i * frameW, 0, frameW, frameH);
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`#${frames[i].index + 1}/${total}`, i * frameW + frameW / 2, frameH + 16);
      if (i > 0) {
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.beginPath();
        ctx.moveTo(i * frameW, 0);
        ctx.lineTo(i * frameW, frameH);
        ctx.stroke();
      }
    });

    if (opts.watermark) _drawWatermark(ctx, strip.width, strip.height, opts.watermark);

    const format = opts.format === 'webp' ? 'image/webp' : 'image/png';
    log.debug(`[Screenshot] Comparison strip: ${valid.length} frames`);
    return strip.toDataURL(format, 0.95);
  } catch (e) { log.warn(`[viewportScreenshot] createComparisonStrip failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  СРАВНЕНИЕ ДО/ПОСЛЕ
// ══════════════════════════════════════════════════════════════

/**
 * Создать изображение со сравнением двух скриншотов (до/после).
 * @param {string} viewportId
 * @param {Object} beforeDataUrl — data URL скриншота ДО
 * @param {Object} [opts]
 * @returns {Promise<string|null>}
 */
export async function createBeforeAfterComparison(viewportId, beforeDataUrl, opts = {}) {
  const afterDataUrl = screenshotViewport(viewportId, { overlay: false });
  if (!beforeDataUrl || !afterDataUrl) return null;

  const [beforeImg, afterImg] = await Promise.all([beforeDataUrl, afterDataUrl].map(url =>
    new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = url; })
  ));
  if (!beforeImg || !afterImg) return null;

  const maxW = Math.max(beforeImg.width, afterImg.width);
  const maxH = Math.max(beforeImg.height, afterImg.height);
  const gap = 4;
  const labelH = 24;

  const canvas = document.createElement('canvas');
  canvas.width = maxW * 2 + gap;
  canvas.height = maxH + labelH;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(beforeImg, 0, labelH, maxW, maxH);
  ctx.drawImage(afterImg, maxW + gap, labelH, maxW, maxH);

  // Разделительная линия
  ctx.strokeStyle = '#ff4444';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(maxW + gap / 2, labelH);
  ctx.lineTo(maxW + gap / 2, canvas.height);
  ctx.stroke();

  // Подписи
  ctx.fillStyle = '#ff4444';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(opts.beforeLabel || 'ДО', maxW / 2, 18);
  ctx.fillStyle = '#44ff44';
  ctx.fillText(opts.afterLabel || 'ПОСЛЕ', maxW + gap + maxW / 2, 18);

  // Процент изменения если задан
  if (opts.percentChange != null) {
    const changeText = `${opts.percentChange > 0 ? '+' : ''}${opts.percentChange}%`;
    const changeColor = Math.abs(opts.percentChange) > 20 ? '#ff4444' : Math.abs(opts.percentChange) > 10 ? '#ffaa00' : '#44ff44';
    ctx.fillStyle = changeColor;
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText(changeText, canvas.width / 2, canvas.height - 10);
  }

  return canvas.toDataURL('image/png', 0.95);
}

// ══════════════════════════════════════════════════════════════
//  BATCH EXPORT + ZIP
// ══════════════════════════════════════════════════════════════

export async function batchExportSlices(viewportId, opts = {}) {
  try { (await import('../ui/exportModule.js')).отметитьЭкспорт('пакет срезов'); } catch (e) { /* история — не повод */ }
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId);
    if (!vp) return [];

    const isStack = typeof vp.setImageIdIndex === 'function';
    let total = isStack ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 0);
    if (!total) { showToast('⚠️ Нет срезов для экспорта', 'warning', 2000); return []; }

    const step = opts.step || 1;
    const results = [];

    if (isStack) {
      const originalIdx = vp.getCurrentImageIdIndex?.() || 0;
      for (let i = 0; i < total; i += step) {
        vp.setImageIdIndex(i);
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false, format: opts.format });
        if (shot) results.push({ index: i, dataUrl: shot });
        if (opts.onProgress) opts.onProgress(results.length, Math.ceil(total / step));
      }
      vp.setImageIdIndex(originalIdx);
    } else {
      const originalIdx = vp.getSliceIndex?.() || 0;
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(0); } catch {}
      vp.render();
      await new Promise(r => setTimeout(r, 80));

      for (let i = 0; i < total; i += step) {
        try {
          if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(i);
          else vp.scroll(i - (vp.getSliceIndex?.() || 0));
        } catch {}
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false, format: opts.format });
        if (shot) results.push({ index: i, dataUrl: shot });
        if (opts.onProgress) opts.onProgress(results.length, Math.ceil(total / step));
      }
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(originalIdx); } catch {}
    }
    vp.render();

    log.debug(`[Screenshot] Batch: ${results.length} slices exported`);

    // Если запрошен ZIP
    if (opts.zip) {
      return await _createZipFromSlices(results, opts);
    }

    return results;
  } catch (e) { log.warn(`[viewportScreenshot] batchExportSlices failed:`, e.message); return []; }
}

async function _createZipFromSlices(slices, opts) {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const ext = opts.format === 'webp' ? 'webp' : 'png';

    for (const slice of slices) {
      const base64 = slice.dataUrl.split(',')[1];
      zip.file(`slice_${String(slice.index).padStart(4, '0')}.${ext}`, base64, { base64: true });
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    _downloadDataUrl(url, `slices_${Date.now()}.zip`);
    URL.revokeObjectURL(url);
    showToast(`📦 ${slices.length} срезов упаковано в ZIP`, 'success', 2000);
    return slices;
  } catch (e) {
    log.warn('[Screenshot] JSZip not available:', e.message);
    return slices;
  }
}

// ══════════════════════════════════════════════════════════════
//  ANIMATED GIF из CINE
// ══════════════════════════════════════════════════════════════

/**
 * Экспортировать CINE-диапазон в анимированный GIF.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {number} [opts.from=0] — начальный срез
 * @param {number} [opts.to] — конечный срез (по умолчанию: все)
 * @param {number} [opts.delay=100] — задержка между кадрами (мс)
 * @param {number} [opts.width] — ширина GIF (по умолчанию: оригинал)
 * @returns {Promise<Blob|null>}
 */
export async function exportAnimatedGIF(viewportId, opts = {}) {
  try {
    const { default: GIF } = await import('gif.js');
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId);
    if (!vp) return null;

    const isStack = typeof vp.setImageIdIndex === 'function';
    const total = isStack ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 100);
    const from = opts.from || 0;
    const to = Math.min(opts.to || total - 1, total - 1);
    const delay = opts.delay || 100;
    const gifWidth = opts.width || (vp.canvas?.width || 512);

    const gif = new GIF({
      workers: 2,
      quality: 10,
      width: gifWidth,
      height: Math.round(gifWidth * (vp.canvas?.height || 512) / (vp.canvas?.width || 512)),
      // [10.19.10] Был '/libs/gif.worker.js' — папки libs в проекте нет, файл
      // не находился, и GIF молча не получался. Адрес теперь даёт сборщик.
      workerScript: АДРЕС_РАБОЧЕГО_ФАЙЛА,
    });

    const originalIdx = isStack ? (vp.getCurrentImageIdIndex?.() || 0) : (vp.getSliceIndex?.() || 0);

    showToast('🎬 Создание GIF...', 'info', 2000);

    for (let i = from; i <= to; i++) {
      if (isStack) vp.setImageIdIndex(i);
      else {
        try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(i); } catch {}
      }
      vp.render();
      await new Promise(r => setTimeout(r, 80));

      const dataUrl = screenshotViewport(viewportId, { overlay: false });
      if (dataUrl) {
        const img = await new Promise(r => { const im = new Image(); im.onload = () => r(im); im.onerror = () => r(null); im.src = dataUrl; });
        if (img) gif.addFrame(img, { delay, copy: true });
      }
    }

    // Восстановление
    if (isStack) vp.setImageIdIndex(originalIdx);
    else {
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(originalIdx); } catch {}
    }
    vp.render();

    return new Promise((resolve, reject) => {
      gif.on('finished', (blob) => {
        showToast('✅ GIF создан', 'success', 2000);
        resolve(blob);
      });
      gif.on('error', reject);
      gif.render();
    });
  } catch (e) {
    log.warn('[Screenshot] GIF export failed:', e.message);
    showToast('❌ GIF: библиотека не загружена', 'error', 2000);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════
//  SCREEN RECORDING
// ══════════════════════════════════════════════════════════════

let _screenRecorder = null;
let _screenRecordingChunks = [];

/**
 * Начать запись экрана viewport'а.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {number} [opts.fps=30]
 * @param {string} [opts.mimeType='video/webm']
 * @returns {boolean}
 */
export function startScreenRecording(viewportId, opts = {}) {
  if (_screenRecorder) return false;

  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return false;

  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return false;

  try {
    const fps = opts.fps || 30;
    const mimeType = opts.mimeType || 'video/webm';
    const stream = canvas.captureStream(fps);
    _screenRecordingChunks = [];
    _screenRecorder = new MediaRecorder(stream, { mimeType });
    _screenRecorder.ondataavailable = (e) => _screenRecordingChunks.push(e.data);
    _screenRecorder.start();
    showToast('🔴 Запись экрана...', 'info', 2000);
    return true;
  } catch (e) {
    log.warn('[Screenshot] Screen recording failed:', e.message);
    return false;
  }
}

/**
 * Остановить запись и вернуть Blob.
 * @returns {Promise<Blob|null>}
 */
export function stopScreenRecording() {
  if (!_screenRecorder) return null;

  return new Promise((resolve) => {
    _screenRecorder.onstop = () => {
      const blob = new Blob(_screenRecordingChunks, { type: _screenRecorder.mimeType });
      _screenRecorder = null;
      _screenRecordingChunks = [];
      showToast('⏹ Запись сохранена', 'success', 2000);
      resolve(blob);
    };
    _screenRecorder.stop();
  });
}

// ══════════════════════════════════════════════════════════════
//  BULK EXPORT METADATA (CSV)
// ══════════════════════════════════════════════════════════════

/**
 * Экспортировать CSV с метаданными для каждого среза.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @returns {Promise<string|null>} CSV строка
 */
export async function exportSliceMetadataCSV(viewportId, opts = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;

  const isStack = typeof vp.setImageIdIndex === 'function';
  const rows = [['SliceIndex', 'InstanceNumber', 'SliceLocation', 'ImagePositionPatient', 'WindowCenter', 'WindowWidth', 'MeanHU', 'StdHU']];

  if (isStack) {
    const ids = vp.getImageIds?.() || [];
    const originalIdx = vp.getCurrentImageIdIndex?.() || 0;

    for (let i = 0; i < ids.length; i++) {
      vp.setImageIdIndex(i);
      vp.render();
      await new Promise(r => setTimeout(r, 50));

      const img = cornerstone.cache.getImage?.(ids[i]);
      const meta = cornerstone.metaData.get('imagePlaneModule', ids[i]);
      const voi = cornerstone.metaData.get('voiLutModule', ids[i]);

      let meanHU = '', stdHU = '';
      const pd = img?.getPixelData?.();
      if (pd) {
        const step = Math.max(1, Math.floor(pd.length / 50000));
        let sum = 0, count = 0, sumSq = 0;
        for (let j = 0; j < pd.length; j += step) {
          const val = pd[j] * (img.slope || 1) + (img.intercept || 0);
          sum += val;
          sumSq += val * val;
          count++;
        }
        meanHU = (sum / count).toFixed(1);
        stdHU = Math.sqrt(sumSq / count - (sum / count) ** 2).toFixed(1);
      }

      rows.push([
        i,
        meta?.InstanceNumber || '',
        meta?.sliceLocation?.toFixed?.(2) || '',
        meta?.imagePositionPatient?.map(v => v.toFixed(2)).join(',') || '',
        voi?.windowCenter || '',
        voi?.windowWidth || '',
        meanHU,
        stdHU,
      ]);
    }

    vp.setImageIdIndex(originalIdx);
    vp.render();
  }

  return rows.map(r => r.join(',')).join('\n');
}

// ══════════════════════════════════════════════════════════════
//  WATERMARK
// ══════════════════════════════════════════════════════════════

function _drawWatermark(ctx, w, h, watermark) {
  ctx.save();
  ctx.globalAlpha = watermark.opacity || 0.15;
  ctx.font = `${watermark.fontSize || Math.round(h * 0.06)}px sans-serif`;
  ctx.fillStyle = watermark.color || '#ffffff';
  ctx.textAlign = 'center';
  ctx.translate(w / 2, h / 2);
  ctx.rotate(watermark.angle || -0.3);
  ctx.fillText(watermark.text || 'CONFIDENTIAL', 0, 0);
  ctx.restore();
}

// ══════════════════════════════════════════════════════════════
//  OVERLAY (с кастомизацией)
// ══════════════════════════════════════════════════════════════

function _drawOverlay(ctx, w, h, viewportId, patientInfo, overlayOpts = {}) {
  const showOrientation = overlayOpts.orientation !== false;
  const showPatient = overlayOpts.patient !== false;
  const showTimestamp = overlayOpts.timestamp !== false;
  const showAnnotations = overlayOpts.annotations !== false;

  ctx.font = `${Math.max(14, Math.round(h * 0.022))}px monospace`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = 3;

  // Orientation markers
  if (showOrientation) {
    const vpEntry = state.viewports.get(viewportId);
    if (vpEntry?.orientation !== undefined) {
      const m = _getOrientationMarkers(vpEntry.orientation);
      if (m) {
        ctx.textAlign = 'center';
        ctx.fillText(m.top, w / 2, 22);
        ctx.fillText(m.bottom, w / 2, h - 8);
        ctx.textAlign = 'left';
        ctx.fillText(m.left, 8, h / 2);
        ctx.textAlign = 'right';
        ctx.fillText(m.right, w - 8, h / 2);
      }
    }
  }

  // Patient info
  if (showPatient && patientInfo) {
    ctx.textAlign = 'left';
    ctx.shadowBlur = 2;
    const lines = [patientInfo.name, patientInfo.id && `ID: ${patientInfo.id}`, patientInfo.date, patientInfo.institution].filter(Boolean);
    const lh = Math.max(16, Math.round(h * 0.024));
    lines.forEach((l, i) => ctx.fillText(l, 10, h - 10 - (lines.length - 1 - i) * lh));
  }

  // Timestamp
  if (showTimestamp) {
    ctx.textAlign = 'right';
    ctx.font = `${Math.max(11, Math.round(h * 0.016))}px monospace`;
    ctx.fillText(new Date().toLocaleString('ru-RU'), w - 10, 20);
  }

  // Annotations overlay (текущие измерения)
  if (showAnnotations) {
    try {
      const allAnns = cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
      const vpAnns = allAnns.filter(a => a.metadata?.viewportId === viewportId);
      if (vpAnns.length > 0) {
        ctx.textAlign = 'right';
        ctx.fillStyle = 'rgba(255,255,100,0.9)';
        ctx.font = `${Math.max(10, Math.round(h * 0.014))}px monospace`;
        let yOff = 40;
        for (const ann of vpAnns.slice(0, 10)) {
          const label = ann.metadata?.toolName || ann.type || 'Measurement';
          ctx.fillText(label, w - 10, yOff);
          yOff += 16;
        }
      }
    } catch (e) { /* annotations unavailable */ }
  }

  ctx.shadowBlur = 0;
}

function _getOrientationMarkers(o) {
  return ({
    [OrientationAxis.AXIAL]:    { top: 'A', bottom: 'P', left: 'R', right: 'L' },
    [OrientationAxis.SAGITTAL]: { top: 'S', bottom: 'I', left: 'A', right: 'P' },
    [OrientationAxis.CORONAL]:  { top: 'S', bottom: 'I', left: 'R', right: 'L' },
  })[o] || null;
}

function _downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ══════════════════════════════════════════════════════════════
//  DICOM SECONDARY CAPTURE
// ══════════════════════════════════════════════════════════════

export function exportDICOMSecondaryCapture(viewportId, meta = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return null;
  const w = canvas.width, h = canvas.height;
  const offscreen = document.createElement('canvas');
  offscreen.width = w; offscreen.height = h;
  const ctx = offscreen.getContext('2d');
  try { ctx.drawImage(canvas, 0, 0); } catch { return null; }
  const imgData = ctx.getImageData(0, 0, w, h);
  const rgbData = new Uint8Array(w * h * 3);
  for (let i = 0, j = 0; i < imgData.data.length; i += 4, j += 3) {
    rgbData[j] = imgData.data[i];
    rgbData[j + 1] = imgData.data[i + 1];
    rgbData[j + 2] = imgData.data[i + 2];
  }

  const uid = () => `1.2.826.0.1.3680043.8.1055.${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');

  // [10.19.10] ЗДЕСЬ НЕ ХВАТАЛО ГРУППЫ 0002 — файл был формально невалидным.
  //
  // По PS3.10 файл DICOM состоит из преамбулы, метки DICM и МЕТАГРУППЫ
  // File Meta Information (0002,xxxx), где обязательны длина группы, версия,
  // класс и экземпляр SOP и синтаксис передачи. Их не было вовсе: сразу за
  // «DICM» шёл набор данных. Наш собственный читатель такой файл возьмёт, а
  // строгий приёмник (станция, PACS) отвергнет — и врач узнает об этом там, а
  // не здесь. Метагруппа собирается ниже и согласована с набором: экземпляр
  // SOP в (0002,0003) тот же, что в (0008,0018).
  const sopInstanceUID = meta.sopInstanceUID || uid();
  const SOP_CLASS_SC = '1.2.840.10008.5.1.4.1.1.7';   // Secondary Capture Image Storage
  const TS_EXPLICIT_LE = '1.2.840.10008.1.2.1';

  const метаЭлементы = [
    _dcmTag(0x0002, 0x0002, 'UI', SOP_CLASS_SC),        // MediaStorageSOPClassUID
    _dcmTag(0x0002, 0x0003, 'UI', sopInstanceUID),      // MediaStorageSOPInstanceUID
    _dcmTag(0x0002, 0x0010, 'UI', TS_EXPLICIT_LE),      // TransferSyntaxUID
    _dcmTag(0x0002, 0x0012, 'UI', '1.2.826.0.1.3680043.8.1055.1'),  // ImplementationClassUID
    _dcmTag(0x0002, 0x0013, 'SH', 'MIKHMEL4D'),         // ImplementationVersionName
  ];
  // FileMetaInformationVersion (0002,0001) — VR OB, ровно два байта 00 01.
  const метаВерсия = _dcmOB(0x0002, 0x0001, new Uint8Array([0x00, 0x01]));
  const длинаМеты = метаВерсия.length + метаЭлементы.reduce((s, e) => s + e.length, 0);

  const elems = [
    _dcmTag(0x0010, 0x0010, 'PN', meta.patientName || 'Anonymous'),
    _dcmTag(0x0010, 0x0020, 'LO', meta.patientId || '000000'),
    _dcmTag(0x0020, 0x000D, 'UI', meta.studyInstanceUID || uid()),
    _dcmTag(0x0008, 0x0020, 'DA', dateStr),
    _dcmTag(0x0008, 0x0030, 'TM', timeStr),
    _dcmTag(0x0020, 0x000E, 'UI', meta.seriesInstanceUID || uid()),
    _dcmTag(0x0008, 0x103E, 'LO', meta.seriesDescription || 'Screen Capture'),
    _dcmTag(0x0008, 0x0060, 'CS', 'SC'), // Modality = SC (Secondary Capture)
    _dcmTag(0x0008, 0x0016, 'UI', SOP_CLASS_SC),
    _dcmTag(0x0008, 0x0018, 'UI', sopInstanceUID),
    _dcmUS(0x0028, 0x0010, h), _dcmUS(0x0028, 0x0011, w),
    _dcmUS(0x0028, 0x0002, 3), _dcmUS(0x0028, 0x0100, 8),
    _dcmUS(0x0028, 0x0101, 8), _dcmUS(0x0028, 0x0102, 7),
    _dcmUS(0x0028, 0x0103, 0), _dcmTag(0x0028, 0x0004, 'CS', 'RGB'),
    _dcmUS(0x0028, 0x0006, 0),
  ];

  const blob = new Blob([
    new Uint8Array(128),                                  // преамбула
    new Uint8Array([0x44, 0x49, 0x43, 0x4D]),             // DICM
    _dcmUL(0x0002, 0x0000, длинаМеты),                    // длина метагруппы
    метаВерсия,
    ...метаЭлементы,
    ...elems,
    _dcmPixelData(rgbData),
  ], { type: 'application/dicom' });

  return blob;
}

export function downloadDICOMCapture(viewportId, filename, meta = {}) {
  const blob = exportDICOMSecondaryCapture(viewportId, meta);
  if (!blob) { showToast('❌ Ошибка DICOM', 'error', 3000); return; }
  const url = URL.createObjectURL(blob);
  _downloadDataUrl(url, filename || `SC_${Date.now()}.dcm`);
  URL.revokeObjectURL(url);
  showToast('📀 DICOM SC сохранён', 'success', 2000);
}

// DICOM helpers
function _dcmTag(g, e, vr, val) {
  let v = val || '';
  if (vr === 'UI' && v.length % 2) v += '\0';
  else if (v.length % 2) v += ' ';
  const buf = new ArrayBuffer(8 + v.length);
  const dv = new DataView(buf);
  dv.setUint16(0, g, true); dv.setUint16(2, e, true);
  dv.setUint8(4, vr.charCodeAt(0)); dv.setUint8(5, vr.charCodeAt(1));
  dv.setUint16(6, v.length, true);
  const arr = new Uint8Array(buf);
  for (let i = 0; i < v.length; i++) arr[8 + i] = v.charCodeAt(i);
  return arr;
}
function _dcmUS(g, e, val) {
  const buf = new ArrayBuffer(10);
  const dv = new DataView(buf);
  dv.setUint16(0, g, true); dv.setUint16(2, e, true);
  dv.setUint8(4, 0x55); dv.setUint8(5, 0x53);
  dv.setUint16(6, 2, true); dv.setUint16(8, val, true);
  return new Uint8Array(buf);
}
/**
 * [10.19.10] UL — беззнаковое 32-битное. Нужно для (0002,0000)
 * FileMetaInformationGroupLength: без него метагруппы не собрать, а без
 * метагруппы файл по PS3.10 невалиден.
 */
function _dcmUL(g, e, val) {
  const buf = new ArrayBuffer(12);
  const dv = new DataView(buf);
  dv.setUint16(0, g, true); dv.setUint16(2, e, true);
  dv.setUint8(4, 0x55); dv.setUint8(5, 0x4C);          // 'UL'
  dv.setUint16(6, 4, true); dv.setUint32(8, val, true);
  return new Uint8Array(buf);
}

/**
 * [10.19.10] OB — двоичное с длинной формой длины (VR + 2 зарезервированных
 * байта + 4 байта длины). Нужно для (0002,0001) FileMetaInformationVersion.
 */
function _dcmOB(g, e, bytes) {
  const c = new Uint8Array(12 + bytes.length);
  const dv = new DataView(c.buffer);
  dv.setUint16(0, g, true); dv.setUint16(2, e, true);
  dv.setUint8(4, 0x4F); dv.setUint8(5, 0x42);          // 'OB'
  dv.setUint16(6, 0, true);                            // зарезервировано
  dv.setUint32(8, bytes.length, true);
  c.set(bytes, 12);
  return c;
}

function _dcmPixelData(rgb) {
  const h = new ArrayBuffer(12);
  const dv = new DataView(h);
  dv.setUint16(0, 0x7FE0, true); dv.setUint16(2, 0x0010, true);
  dv.setUint8(4, 0x4F); dv.setUint8(5, 0x42);
  dv.setUint16(6, 0, true); dv.setUint32(8, rgb.length, true);
  const c = new Uint8Array(12 + rgb.length);
  c.set(new Uint8Array(h)); c.set(rgb, 12);
  return c;
}

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyScreenshot() {
  if (_screenRecorder) {
    try { _screenRecorder.stop(); } catch {}
    _screenRecorder = null;
  }
  _screenRecordingChunks = [];
}